import React, { type ReactNode } from 'react';
import { EmptyState, type EmptyStateProps } from './empty-state';
import { ErrorState } from './error-state';
import { LoadingSkeleton } from './loading-skeleton';
import { Card, CardContent } from './card';
import { cn } from '../lib/utils';
import { PageTitle } from './typography';

export interface DashboardPageHeaderProps {
  title: string;
  description: string;
  actions?: ReactNode;
  /**
   * Optional slot rendered inline with the title (typically a small help /
   * info icon that opens a Popover explaining the screen). Used by the
   * `AboutThisScreen` pattern to move explanatory walls off the page.
   */
  infoSlot?: ReactNode;
  className?: string;
}

export function DashboardPageHeader({
  title,
  description,
  actions,
  infoSlot,
  className,
}: DashboardPageHeaderProps) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0 max-w-4xl">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <PageTitle className="min-w-0 break-words font-heading text-page-title leading-tight text-text-heading">
            {title}
          </PageTitle>
          {infoSlot ? <div className="flex items-center">{infoSlot}</div> : null}
        </div>
        <p className="experience-candidate-body mt-1 break-words text-sm text-slate-600">
          {description}
        </p>
      </div>
      {actions ? (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:max-w-[44rem] sm:justify-end [&>*]:min-w-0">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export interface DashboardLoadingStateProps extends DashboardPageHeaderProps {
  cards?: number;
  lines?: number;
}

export function DashboardLoadingState({
  title,
  description,
  actions,
  cards = 4,
  lines = 3,
  className,
}: DashboardLoadingStateProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <DashboardPageHeader title={title} description={description} actions={actions} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: cards }).map((_, index) => (
          <Card key={index}>
            <CardContent className="pt-5">
              <LoadingSkeleton lines={lines} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export interface DashboardErrorStateProps extends DashboardPageHeaderProps {
  message: string;
  onRetry?: () => void;
}

export function DashboardErrorState({
  title,
  description,
  actions,
  message,
  onRetry,
  className,
}: DashboardErrorStateProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <DashboardPageHeader title={title} description={description} actions={actions} />
      <ErrorState message={message} onRetry={onRetry} />
    </div>
  );
}

export interface DashboardEmptyStateProps
  extends DashboardPageHeaderProps, Omit<EmptyStateProps, 'title' | 'description' | 'className'> {
  emptyTitle?: string;
  emptyDescription?: string;
  emptyClassName?: string;
  emptyActions?: ReactNode;
}

export function DashboardEmptyState({
  title,
  description,
  actions,
  className,
  emptyTitle,
  emptyDescription,
  emptyClassName,
  emptyActions,
  ...emptyStateProps
}: DashboardEmptyStateProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <DashboardPageHeader title={title} description={description} actions={actions} />
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        className={emptyClassName}
        actions={emptyActions}
        {...emptyStateProps}
      />
    </div>
  );
}
